import { ErrorResponse } from '../../../../../common/types/errorRespose';
import { CreateExternalAppDto } from '../../../../../api-client';
import React from 'react';
import { Box, Button, Flexbox, Icon, Modal, TextField, Typography } from '@hiiretail/synergy-ui';
import { ErrorBox } from '../../../../../common/components/ErrorBox/ErrorBox';
import { Controller } from 'react-hook-form';
import { useCreateExternalAppForm } from '../../../hooks/useCreateExternalAppForm';

export type CreateExternalAppModalProps = {
  onClose: () => void;
  isOpen: boolean;
  isCreating: boolean;
  error: ErrorResponse | null;
  createExternalApp: (createCustomRoleDto: CreateExternalAppDto) => void;
};

type CreateCustomRoleModalComponent = React.FC<CreateExternalAppModalProps>;

export const CreateExternalAppModal: CreateCustomRoleModalComponent = ({
  onClose,
  isOpen,
  isCreating,
  error,
  createExternalApp,
}) => {
  const { control, handleSubmit, formState } = useCreateExternalAppForm();

  const onFormSubmit = (formData: CreateExternalAppDto) => {
    const createExternalAppDto = { ...formData };
    if (!createExternalAppDto.apiKey?.length) {
      delete createExternalAppDto.apiKey;
    }
    if (!createExternalAppDto.description?.length) {
      delete createExternalAppDto.description;
    }
    createExternalApp(createExternalAppDto);
  };

  return (
    <Modal
      data-testid="create-external-app-modal"
      title="Create external app"
      open={isOpen}
      onClose={onClose}
      footer={
        <Flexbox container justifyContent="flex-end">
          <Button
            disabled={
              (formState?.errors && !!Object.entries(formState?.errors).length) ||
              Object.keys(formState?.dirtyFields).length < 1 ||
              isCreating
            }
            onClick={handleSubmit(onFormSubmit)}
            type="submit"
          >
            {isCreating ? 'Creating ...' : 'Create'}
          </Button>
        </Flexbox>
      }
    >
      <ErrorBox error={error} />
      <Flexbox container gutter={4} alignItems="flex-start">
        <Flexbox item>
          <Icon.Info size="lg" color={() => '#6E33A9'} />
        </Flexbox>
        <Flexbox item container>
          <Typography variant="body2">
            <strong>External Apps</strong> are specific named entity that are developed to create
            tenant apps. It provides a unique API KEYs functionality to exchange id tokens from
            tenant federated system for Hii Retail IAM tokens.
          </Typography>
        </Flexbox>
      </Flexbox>
      <Box mt={4} />
      <form>
        <Box mb={2}>
          <Controller
            name="name"
            control={control}
            render={({ field: { ref, value, onChange } }) => (
              <TextField
                type="text"
                tabIndex={0}
                fullWidth
                required
                onChange={onChange}
                label="Name"
                name="name"
                ref={ref}
                value={value}
                // @ts-ignore
                hint={
                  <span>
                    Unique name of the external application
                    <br />
                    Must be between 3 and 256 characters long
                  </span>
                }
                error={formState.errors?.name?.message}
              />
            )}
          />
        </Box>
        <Box mb={2}>
          <Controller
            name="description"
            control={control}
            render={({ field: { ref, value, onChange } }) => (
              <TextField
                tabIndex={0}
                fullWidth
                name="description"
                label="Description"
                type="text"
                ref={ref}
                value={value}
                // @ts-ignore
                hint={
                  <span>
                    Describes your app's most notable characteristics
                    <br />
                    Must be between 5 and 256 characters long
                  </span>
                }
                onChange={onChange}
                error={formState.errors?.description?.message}
              />
            )}
          />
        </Box>
        <Box mb={2}>
          <Controller
            name="apiKey"
            control={control}
            render={({ field: { ref, value, onChange } }) => (
              <TextField
                tabIndex={0}
                label="API Key"
                name="apiKey"
                type="text"
                fullWidth
                ref={ref}
                value={value}
                // @ts-ignore
                hint={
                  <span>
                    Key that will give you a possibility to exchange federated token on IAM token
                    <br />
                    The key will be autogenerated if you leave this field empty
                    <br />
                    Must be between 40 and 60 characters long
                  </span>
                }
                onChange={onChange}
                error={formState.errors?.apiKey?.message}
              />
            )}
          />
        </Box>
      </form>
    </Modal>
  );
};
